<i18n src="./translations"></i18n>

<template>
  <section
    class="sidebar"
    :class="classes"
    :data-preload="[$options.name]"
    data-testid="pdp-sidebar"
  >
    <div class="content">
      <PriceInfo21 class="info price" :product="product">
        <button
          class="add-to-wishlist"
          :class="{ '--wished': isInWishlist }"
          data-testid="pdp-add-to-wishlist-btn"
          :aria-label="$t('pdp-sidebar.wishlist-label')"
          @click="handleAddToWishlist"
        >
          <Icon
            :name="isInWishlist ? 'wishlist_filled_heart' : 'heart'"
            width="24"
            height="24"
            aria-hidden="true"
          />
        </button>
      </PriceInfo21>

      <template v-if="!isOTCSalesChannel">
        <QuantitySelector26
          v-if="isPlpOrSearchPage || product.isBuyable"
          class="quantity"
          :has-label="false"
          :has-vertical-divider="false"
          :product="product"
          :value="product.quantityAmount"
          :choices="product.quantityOptions"
          @onQuantityChange="handleQuantityChange($event)"
        />

        <Button
          class="add-to-cart"
          :aria-label="$t('pdp-sidebar.cart-label')"
          :modifiers="['primary', 'full']"
          :on-click="handleCartClick"
          v-bind="
            getAddToCartButtonProps({
              add: $t('pdp-sidebar.cart.add'),
              added: $t('pdp-sidebar.cart.added'),
            })
          "
          :has-loading="addToCartState === AddToCartState.Loading"
          data-testid="pdp-add-to-cart-btn"
        />

        <p class="note">
          {{ $t('pdp-sidebar.note.free-delivery', { freeDeliveryMin }) }} •
          {{ $t('pdp-sidebar.note.returns') }}
        </p>
      </template>
    </div>

    <PdpRelatedPairedArticles :product="product" />

    <PdpFooter is-desktop />

    <Dialog
      ref="unavailableProductDialog"
      class="unavailable-dialog"
      :click-outside="true"
      :title="$t('pdp-sidebar.availability.delayed.title')"
    >
      <div>
        {{
          $t('pdp-sidebar.availability.delayed.message', {
            '%customerServicePhoneNumber%': customerServicePhoneNumber,
          })
        }}
      </div>
    </Dialog>

    <AlternativeProductsDialog
      ref="alternativeProductsDialog"
      :parent-product="product"
      :handle-cancel="handleAlternativeProductCancel"
      :handle-confirm="handleAlternativeProductConfirm"
    />
  </section>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import PriceInfo21 from 'Components/02-molecules/price-info-21/PriceInfo21';
import QuantitySelector26 from '../quantity-selector-26/QuantitySelector26.vue';
import Button from 'Components/01-atoms/button/Button';
import Dialog from 'Components/02-molecules/dialog/Dialog';
import AlternativeProductsDialog from 'Components/03-organisms/alternative-products-dialog/AlternativeProductsDialog';
import { mapState, mapGetters, mapActions } from 'vuex';
import get from 'lodash/get';
import addToCartMixin from 'Libs/mixins/addToCartMixin';
import PdpFooter from 'Components/03-organisms/product-details-26/subcomponents/pdp-footer/PdpFooter';
import PdpRelatedPairedArticles from 'Components/03-organisms/product-details-26/subcomponents/pdp-related-articles/PdpRelatedPairedArticles';
import Icon from 'Src/components/00-generated/Icon.vue';

export default {
  name: 'PdpSidebar',
  components: {
    PriceInfo21,
    QuantitySelector26,
    Button,
    Dialog,
    AlternativeProductsDialog,
    PdpFooter,
    PdpRelatedPairedArticles,
    Icon,
  },
  mixins: [globalMixin, addToCartMixin],
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({
        productId: '',
        quantityOptions: [],
        quantityAmount: 1,
        isBuyable: true,
        availability: '',
        hasCrossReferences: false,
        prices: { price: 0 },
      }),
    },
    relatedItemsLink: {
      type: String,
      default: '',
    },
    pdpCarBar: {
      type: Object,
      default: () => ({ key: 'UNIVERSAL_PRODUCT' }),
    },
    isListingPage: { type: Boolean, default: false },
  },
  data() {
    return {};
  },
  computed: {
    ...mapState('core', {
      viewport: ({ viewport }) => viewport.range,
      companyId: (state) => state.requestData.companyId,
      freeDeliveryMin: ({ freeDeliveryMin }) => freeDeliveryMin,
    }),
    ...mapGetters('core', ['customerServicePhoneNumber', 'isOTCSalesChannel']),
    ...mapState('productsList', {
      isPlpOrSearchPage: (state) => {
        const type = get(state, 'data.plp.pageType');
        return (
          ['productListing', 'search', 'brandProductListing'].indexOf(type) !==
          -1
        );
      },
    }),
    ...mapGetters('wishlist', { checkIsInWishlist: 'isInWishlist' }),
    isInWishlist() {
      return this.checkIsInWishlist(this.product);
    },
  },
  methods: {
    ...mapActions('wishlist', ['addToWishlist', 'removeFromWishlist']),
    handleCartClick() {
      this.handleAddToCart(this.selectedQuantity);
    },
    handleAddToWishlist() {
      if (this.isInWishlist) {
        this.removeFromWishlist(this.product);
      } else {
        this.addToWishlist({
          product: this.product,
          quantity: this.selectedQuantity,
          list: this.isListingPage
            ? 'Product Listing Page'
            : 'Product Detail Page',
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.sidebar {
  top: 0;
  align-self: flex-start;
  padding: var(--space-1) 0;
  background-color: var(--color-white);

  .content {
    position: relative;
    border-radius: var(--space-1);
    background-color: var(--color-white);

    .add-to-wishlist {
      cursor: pointer;
      padding: 0;

      ::v-deep .icon {
        color: var(--color-kfzteile24-secondary);
      }

      &.--wished ::v-deep .icon {
        fill: var(--color-kfzteile24-secondary);
      }
    }

    .quantity {
      width: 100%;
      margin-top: var(--space-0--half);

      ::v-deep {
        .input {
          height: var(--row-height);
          padding-top: 0;
        }
      }
    }

    .add-to-cart {
      height: var(--row-height);
      margin-top: var(--space-1);
      flex-grow: 1;

      ::v-deep {
        .inner {
          @media #{$_mediaMDown} {
            padding: 0 var(--space-1);
          }
        }
      }
    }

    .note {
      color: var(--color-charcoal-gray);
      font-size: var(--font-size-S);
      text-align: center;
      margin-top: var(--space-1--half);
    }
  }

  @media #{$_mediaMDown} {
    max-width: unset;
    width: 100%;

    &::after {
      display: none;
    }

    .content {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}

@media #{$_mediaLUp} {
  .sidebar {
    position: sticky;

    .content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: var(--space-3);
      border: 1px solid var(--color-rebranding-frenchGray);
    }
  }
}
</style>
